import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ArrowLeftIcon, SendIcon } from 'lucide-react';
import { Fragment } from 'react';

import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input, InputPassword } from '@/components/ui/input';

const bitbucketServerFormSchema = z.object({
  accountName: z.string().min(1, 'Enter a name for this integration'),
  username: z.string().min(1, 'Enter your Bitbucket username'),
  password: z.string().min(1, 'Enter your app password'),
  accountUrl: z.string().min(1, 'Enter your Bitbucket Server instance URL'),
});

export type IBitbucketServerForm = z.infer<typeof bitbucketServerFormSchema>;

type Props = {
  onSubmit: (data: IBitbucketServerForm) => Promise<void>;
  onBack: () => void;
  accountName?: string;
  accountUrl?: string;
  btnText?: React.ReactNode;
};

const BitbucketServerForm = ({ onSubmit, onBack, accountName = '', btnText = 'Submit', accountUrl = '' }: Props) => {
  const form = useForm<IBitbucketServerForm>({
    resolver: zodResolver(bitbucketServerFormSchema),
    defaultValues: {
      accountName,
      username: '',
      password: '',
      accountUrl,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="accountName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Integration name</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="e.g., My Bitbucket Server Org name" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="accountUrl"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Bitbucket Server instance URL</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="bitbucket.yourcompany.com" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Username</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="your-username" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>App password</FormLabel>
                <FormControl>
                  <InputPassword {...field} placeholder="Paste your app password here" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
          <Button size="sm" variant="outline" className="sm:flex-1" onClick={onBack}>
            <ArrowLeftIcon /> View instructions
          </Button>
          <Button
            type="submit"
            size="sm"
            isLoading={form.formState.isSubmitting}
            disabled={form.formState.isSubmitting}
            className="sm:flex-1"
          >
            {btnText ?? (
              <Fragment>
                Submit <SendIcon />
              </Fragment>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default BitbucketServerForm;
